import React, { useContext, useState, useMemo } from "react";
import { useLazyLoadQuery, graphql } from "react-relay";
import { Box, Button, Container, Tooltip, Typography, styled } from "@mui/material";

import GeneralTaskDetailDrawer from "../../components/diagnosticReportCard/GeneralTaskDetailDrawer";
import DiagnosticReportCard from "../../components/diagnosticReportCard/DiagnosticReportCard";
import FavouritesList from "./FavouritesList";
import FavouritesContext from "../../contexts/LocalStorageContext";
import { FavouritesQuery } from "./__generated__/FavouritesQuery.graphql";
import { useShortcut } from "../../hooks/useShortcut";
import SimpleBackdrop from "../../components/common/SimpleBackdrop";
import PageContainer from "../../components/common/PageContainer";
import DisableHotkeyContext from "../../contexts/DisableHotkeyContext";

const TitleContainer = styled(Container)({
  justifyContent: "center",
  textAlign: "center",
  width: "100%",
});

const ButtonBox = styled(Box)({
  display: "flex",
  justifyContent: "auto",
  margin: "10px",
  width: "96.5%",
});

const StyledButton = styled(Button)({
  marginLeft: "10px",
});

const Favourites: React.FC = () => {
  const { favouriteIdArray } = useContext(FavouritesContext);

  const [showList, setShowList] = useState(true);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [currentImage, setCurrentImage] = useState<number>(0);
  const { disableHotkey } = useContext(DisableHotkeyContext);

  const data = useLazyLoadQuery<FavouritesQuery>(favouritesQuery, { ids: favouriteIdArray });

  const [reportTab, setReportTab] = useState<number>(0);

  const currentGeneralTask = useMemo(
    () => data?.nodes?.result?.edges[currentImage]?.node?.produced_by?.parents?.edges[0]?.node?.parent,
    [data, currentImage],
  );

  const handleChangeCurrentImage = (index: number) => {
    setCurrentImage(index);
  };

  useShortcut(() => setShowList((v) => !v), ["s"], disableHotkey);
  useShortcut(() => setOpenDrawer((v) => !v), ["d"], disableHotkey);

  return (
    <PageContainer maxWidth="xl">
      <TitleContainer>
        <Typography sx={{ flexShrink: 0 }} variant="h5" gutterBottom>
          My Solutions
        </Typography>
      </TitleContainer>
      <ButtonBox>
        <Tooltip title="use (s/S) to toggle between views">
          <StyledButton color="inherit" variant="contained" onClick={() => setShowList((v) => !v)}>
            {showList ? "Show Report" : "Show List"}
          </StyledButton>
        </Tooltip>
        {!showList && (
          <Tooltip title="use (d/D) to open/close details">
            <StyledButton color="inherit" variant="contained" onClick={() => setOpenDrawer((v) => !v)}>
              Details
            </StyledButton>
          </Tooltip>
        )}
      </ButtonBox>
      <Container maxWidth="xl">
        {showList ? (
          <FavouritesList data={data} />
        ) : (
          <DiagnosticReportCard
            sweepList={currentGeneralTask?.swept_arguments as string[]}
            modelType={currentGeneralTask?.model_type || ""}
            changeCurrentImage={handleChangeCurrentImage}
            inversionSolutions={data || []}
            reportTab={reportTab}
            setReportTab={setReportTab}
          />
        )}
        {!showList && currentGeneralTask && (
          <GeneralTaskDetailDrawer generalTaskDetails={currentGeneralTask} openDrawer={openDrawer} />
        )}
      </Container>
    </PageContainer>
  );
};

const FavouritesPage: React.FC = () => {
  return (
    <React.Suspense fallback={<SimpleBackdrop />}>
      <Favourites />
    </React.Suspense>
  );
};

export default FavouritesPage;

export const favouritesQuery = graphql`
  query FavouritesQuery($ids: [ID!]) {
    nodes(id_in: $ids) {
      result {
        edges {
          node {
            __typename
            ... on Node {
              id
            }
            ... on FileInterface {
              file_name
              file_url
              file_size
              md5_digest
              meta {
                k
                v
              }
              relations {
                total_count
              }
            }
            ... on InversionSolutionInterface {
              created
              mfd_table {
                id
              }
              mfd_table_id
              tables {
                table_id
                table_type
              }
              produced_by {
                __typename
                ... on Node {
                  id
                }
                ... on AutomationTaskInterface {
                  parents {
                    edges {
                      node {
                        parent {
                          __typename
                          id
                          ... on Node {
                            id
                          }
                          ... on GeneralTask {
                            meta {
                              k
                              v
                            }
                            title
                            id
                            notes
                            description
                            created
                            model_type
                            swept_arguments
                            argument_lists {
                              k
                              v
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;
